import {intervalUntilExists, intervalUntilShadowElementExists} from "./intervalUntilExists";
import intervalUntilCondition from "./intervalUntilCondition";
import { getUrlParameter, runScriptInPageContext } from "./utils";

export const enum SELECTOR_TYPES {
  ELEMENT_TEXT_MATCH = "ELEMENT_TEXT_MATCH",
  URL_PARAM_EXISTS = "URL_PARAM_EXISTS",
  WINDOW_VAR_EXISTS = "WINDOW_VAR_EXISTS",
}
interface CommonConditionalParams {
  giveUpTime?: number;
}
/**
 * This condition may want to be avoided or at least seriously considered before use
 * because it usually won't play nice with internationalization. Great to use when waiting
 * for numbers or non i18n strings to appear.
 */
interface ElementTextMatch extends CommonConditionalParams {
  type: SELECTOR_TYPES.ELEMENT_TEXT_MATCH;
  selector: string;
  regex: string;
}
interface UrlParamExists extends CommonConditionalParams {
  type: SELECTOR_TYPES.URL_PARAM_EXISTS;
  paramKey: string;
}
interface WindowVarExists extends CommonConditionalParams {
  type: SELECTOR_TYPES.WINDOW_VAR_EXISTS;
  script: string;
}
export type ConditionalWaitFor =
  | ElementTextMatch
  | UrlParamExists
  | WindowVarExists;

const handlerConditionalSelectors = (
  selector: ConditionalWaitFor,
  handler: Function
) => {
  let condition: () => boolean;
  switch (selector.type) {
    case SELECTOR_TYPES.ELEMENT_TEXT_MATCH:
      condition = () => {
        const text = document
          .querySelector(selector.selector)
          ?.textContent?.toLowerCase();
        return !!text?.match(new RegExp(selector.regex));
      };
      break;
    case SELECTOR_TYPES.URL_PARAM_EXISTS:
      condition = () => {
        return !!getUrlParameter(selector.paramKey);
      };
      break;
    case SELECTOR_TYPES.WINDOW_VAR_EXISTS:
      condition = () => {
        runScriptInPageContext("g-condition", selector.script);
        let el = document.querySelector(
          "script[id='g-condition']"
        ) as HTMLElement;
        return !!el.dataset.val;
      };
      break;
    default:
      throw new Error("Non-exhausive match for selector");
  }
  return intervalUntilCondition(condition, handler, 500, selector.giveUpTime);
};

export type WaitSelectors = string | (string | ConditionalWaitFor)[];

const routeSelector = (
  selector: string | ConditionalWaitFor,
  handler: Function
) => {
  if (typeof selector === "string") {
    return intervalUntilExists(selector, handler);
  } else {
    return handlerConditionalSelectors(selector, handler);
  }
};

export const waitForSelectors = (
  selectors: WaitSelectors, 
  handler: Function,
  pageDocument: Document | ShadowRoot = document
) => {
  if (typeof selectors === "string") {
    return intervalUntilExists(selectors, handler, undefined, pageDocument);
  } else if (Array.isArray(selectors)) {
    return selectors.reduceRight(
      (acc, selector) => (x: any) => {
        return routeSelector(selector, acc);
      },
      handler
    )();
  } else {
    throw TypeError("unsupported selectors type for waitForSelectors");
  }
};

/**
 * 
 * @param shadowSelector 
 * @param selectors 
 * @param handler 
 * @returns 
 * usage: addRatesPageListener: (handler) => {
    const timeout = waitForSingleShadowSelectors(
      SynxisGtm.selectors.shadow.target,
      SynxisGtm.selectors.ratesPage.waitFor,
      handler
    );
 */
export const waitForSingleShadowSelectors = (shadowSelector: WaitSelectors, selectors: WaitSelectors, handler: Function) => {
  if (typeof shadowSelector === "string" && typeof selectors === "string") {
    // wait until parent shadow dom element exists
    intervalUntilExists(shadowSelector, () => {
      // find element within existing shadow dom 
      return intervalUntilShadowElementExists(document.querySelector(shadowSelector), selectors, handler);
    });
  // TODO: modify chunk below to compensate shadowRoots
  // Leaving untouched for now since we largely don't use non-string selectors
  } else if (Array.isArray(selectors)) {
    return selectors.reduceRight(
      (acc, selector) => (x: any) => {
        return routeSelector(selector, acc);
      },
      handler
    )();
  } else {
    throw TypeError("unsupported selectors type for waitForSingleShadowSelectors");
  }
};

export default waitForSelectors;
